import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {AuthenticationService_LogInWithCodeLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/authentication/v1/service.grpc.lazy';
import {GetSSOButtonsResponse_SSOButton} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/authentication/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {Log as Logger} from '@aboutyou/mlm';
import {useShowToast} from '@aboutyou/ui-components';
import type {LoggedInUserDataWithToken} from '@aboutyou/user';
import {useInterval} from '@aboutyou/utils';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
  SSO_CALLBACK_EVENT,
  getSSOChannel,
} from '../../../SSOCallbackMiddleware/constants';
import {useAuthLogger} from '../../../hooks/useAuthLogger';
import {
  SkipErrorLogging,
  getAuthErrorMetadata,
  useAuthResponseHandler,
} from '../../../hooks/useAuthResponseHandler';
import {PLACEHOLDER_HTML} from './PopupPlaceholder';

type Props = {
  isSignUp: boolean;
  disableSuccessToast: boolean;
  onError: ((e: string) => void) | null;
  onSuccess: ((customer: LoggedInUserDataWithToken) => void) | null;
};

interface CallbackData {
  code: string | null;
  state: string | null;
  error: string | null;
  error_description: string | null;
}

type PopupCommunicationChannel = 'PostMessage' | 'BroadcastChannel' | 'NA';

export const useSSOCallbackHandler = ({
  isSignUp,
  disableSuccessToast,
  onError,
  onSuccess,
}: Props) => {
  const {t} = useTranslation();
  const showToast = useShowToast();
  const popupWindowRef = useRef<Window | null>(null);
  const [isWaitingOnIDPPopup, setIsWaitingOnIDPPopup] = useState(false);
  const [isRedirectingToSSO, setIsRedirectingToSSO] = useState(false);

  const onSuccessRef = useRef<
    ((customer: LoggedInUserDataWithToken) => void) | null
  >(onSuccess);
  const onErrorRef = useRef<(e: string) => void>(onError);

  const currentIdpRef = useRef<{
    idp: string;
    idpKey: string;
  }>(null);

  const method = isSignUp ? 'register' : 'login';

  const {Log, resetLoggerInstanceId} = useAuthLogger();

  const {
    handleAuthResponse,
    loading: isAuthenticating,
    setLoading,
  } = useAuthResponseHandler({
    disableSuccessToast,
  });

  const loginWithCode = useGrpcRequestMutation(
    AuthenticationService_LogInWithCodeLazy,
  );

  const handleIDPError = useCallback(
    ({
      error,
      state,
      error_description,
      popupCommunicationChannel,
    }: {
      state: string | null;
      error: string | null;
      error_description?: string | null;
      popupCommunicationChannel: PopupCommunicationChannel;
    }) => {
      // In SCAYLE's Klarna's implementation, if the user cancels the login by clicking the Close ("X") button on the IDP's popup,
      // they set the error_description to "Login cancelled by user". We check for this specific case to avoid showing an error toast when the user intentionally cancels the login process.
      if (error && error_description?.includes('Login cancelled by user')) {
        Log({
          event: 'sso-cancelled-by-user',
          state,
        });
        return;
      }

      // In SCYALE's Google implementation, if the user cancels the login by clicking the Cancel button on the IDP's popup,
      // they set the error to "access_denied". We check for this specific case to avoid showing an error toast when the user intentionally cancels the login process.
      if (error === 'access_denied') {
        Log({
          event: 'sso-cancelled-by-user',
          state,
        });
        return;
      }

      Log({
        event: 'sso-fe-error',
        state,
        attributes: {
          reason: 'redirect',
          error_detail: `${error ?? 'NA'}:${error_description ?? 'NA'}`,
          popup_communication_channel: popupCommunicationChannel,
        },
      });

      const defaultErrorMessage = t(
        'Sorry, something went wrong! Please try again later.',
      );
      const missingPermissionsErrorMessage = t(
        'You need to give email permissions to be able to log in. Please do so while logging in.',
      );

      /**
       * SCAYLE sets the error_description to IDENTITY_PROVIDER_MISSED_EMAIL if they are not able to get the email from the IDP
       * This can happen if the user did not approve the email permission in the IDP consent screen
       * In this case we show a more specific error message
       */
      const message = error?.includes('IDENTITY_PROVIDER_MISSED_EMAIL')
        ? missingPermissionsErrorMessage
        : defaultErrorMessage;

      showToast({
        type: 'ERROR',
        content: message,
      });

      onErrorRef.current?.(message);
    },
    [Log, t],
  );

  const handleLoginCallError = useCallback((message: string) => {
    showToast({
      type: 'ERROR',
      content: message,
    });
    onErrorRef.current?.(message);
  }, []);

  const handleLoginWithCode = useCallback(
    (
      {code, state}: {code: string; state: string},
      popupCommunicationChannel: PopupCommunicationChannel,
    ) => {
      Log({
        event: 'sso-redirect',
        state,
        attributes: {
          popup_communication_channel: popupCommunicationChannel,
        },
      });

      setLoading(true);

      loginWithCode
        .mutateAsync({
          code,
          state,
        })
        .then(data => {
          Log({
            event: 'sso-success',
            state,
            attributes: {
              popup_communication_channel: popupCommunicationChannel,
            },
          });

          return handleAuthResponse({
            method,
            medium: data.customer.authenticationMethod.type,
          })(data);
        })
        .then(customer => {
          onSuccessRef.current?.(customer);
        })
        .catch(error => {
          if (error instanceof SkipErrorLogging) {
            Log({
              event: 'sso-fe-error',
              state,
              attributes: {
                reason: 'unknown',
                error_detail: `${error.message}:${'NA'}`,
                popup_communication_channel: popupCommunicationChannel,
              },
            });

            return;
          }

          const metadata = getAuthErrorMetadata(error);

          Log({
            event: 'sso-fe-error',
            state,
            attributes: {
              reason: 'login-with-code',
              code: metadata.code ?? null,
              error_detail: `${metadata.message}:${metadata.code}`,
              popup_communication_channel: popupCommunicationChannel,
            },
          });

          handleLoginCallError(
            t('Sorry, something went wrong! Please try again later.'),
          );
        })
        .finally(() => {
          resetLoggerInstanceId();
          setLoading(false);
        });
    },
    [
      Log,
      setLoading,
      loginWithCode,
      handleAuthResponse,
      method,
      handleLoginCallError,
      t,
      resetLoggerInstanceId,
    ],
  );

  const createPopup = useCallback(() => {
    const width = 750;
    const height = 750;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    const popup = window.open(
      '',
      'sso_auth_popup',
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes,status=yes`,
    );

    popup?.document.head.insertAdjacentHTML(
      'afterbegin',
      PLACEHOLDER_HTML.head,
    );
    popup?.document.body.insertAdjacentHTML(
      'afterbegin',
      PLACEHOLDER_HTML.body,
    );
    return popup!;
  }, []);

  const openSSO = ({
    ssoButton,
    onSuccess,
    onError,
    popupInstance,
    openAsPopup,
  }: {
    ssoButton: GetSSOButtonsResponse_SSOButton;
    onError: (e: string) => void;
    onSuccess: (customer: LoggedInUserDataWithToken) => void;
    openAsPopup: boolean;
    popupInstance?: Window;
  }) => {
    Log({
      event: 'sso-open-url',
      state: null,
      attributes: {
        idp_key: ssoButton.idpKey,
        idp: ssoButton.idp,
      },
    });

    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    currentIdpRef.current = {
      idp: ssoButton.idp,
      idpKey: ssoButton.idpKey,
    };
    if (!openAsPopup) {
      setIsRedirectingToSSO(true);
      window.location.href = ssoButton.url;
      // In case of browser back navigation, we reset the redirecting state after some time
      // this is because browsers restore the previous state when navigating back with the same state and the user would see a loading state forever
      setTimeout(() => {
        setIsRedirectingToSSO(false);
      }, 4000);
      return;
    }

    /**
     * normally the user will not be able to open multiple popups as we have a overlay blocking the SSO buttons.
     * But on iOS we are not rendering the overlay due to a limitation in refocusing the popup window.
     *
     * In this case we close any existing popup before opening a new one.
     */
    if (popupWindowRef.current) {
      closeAndResetSSOPopup();
    }

    setIsWaitingOnIDPPopup(true);

    const popup = popupInstance || createPopup();

    popup.location.href = ssoButton.url;

    popupWindowRef.current = popup;

    // If popup was blocked or couldn't be opened
    if (!popup || popup.closed) {
      closeAndResetSSOPopup();
      Logger.unSampledLog('IDP Popup blocked or could not be opened', {
        ssoUrl: ssoButton.url,
      });
    }
  };

  const refocusSSOPopup = () => {
    if (popupWindowRef.current) {
      popupWindowRef.current.focus();
    }
  };

  const closeAndResetSSOPopup = () => {
    if (popupWindowRef.current) {
      try {
        popupWindowRef.current.close();
      } catch (error) {
        // ignore errors when trying to close the window
      }
    }

    setIsWaitingOnIDPPopup(false);
    popupWindowRef.current = null;
  };

  const handleCallbackData = useCallback(
    (
      callbackData: CallbackData,
      popupCommunicationChannel: PopupCommunicationChannel,
    ) => {
      const {code, state, error, error_description} = callbackData;

      if (error || error_description) {
        handleIDPError({
          error,
          error_description,
          state,
          popupCommunicationChannel,
        });
        return;
      }

      if (code && state) {
        handleLoginWithCode({code, state}, popupCommunicationChannel);
      }

      if (!code || !state) {
        handleLoginCallError(
          t('Sorry, something went wrong! Please try again later.'),
        );
      }
    },
    [handleIDPError, handleLoginCallError, handleLoginWithCode, t],
  );

  useEffect(() => {
    if (!isWaitingOnIDPPopup) return;

    const handleOpenerCallback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === SSO_CALLBACK_EVENT) {
        closeAndResetSSOPopup();

        handleCallbackData(event.data.data as CallbackData, 'PostMessage');
      }
    };
    window.addEventListener('message', handleOpenerCallback);
    return () => {
      window.removeEventListener('message', handleOpenerCallback);
    };
  }, [handleCallbackData, isWaitingOnIDPPopup]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    const ssoChannelId = getSSOChannel();
    const channel = new window.BroadcastChannel(ssoChannelId);
    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event.data?.type === SSO_CALLBACK_EVENT) {
        closeAndResetSSOPopup();
        handleCallbackData(event.data.data as CallbackData, 'BroadcastChannel');
      }
    };
    channel.addEventListener('message', handleBroadcastMessage);
    return () => {
      channel.removeEventListener('message', handleBroadcastMessage);
      channel.close();
    };
  }, [handleCallbackData]);

  /**
   * If the user simply closes the popup, we want to reset the loading states
   */
  useInterval(() => {
    if (popupWindowRef.current) {
      if (popupWindowRef.current.closed) {
        closeAndResetSSOPopup();
        Log({
          event: 'sso-cancelled-by-user',
          state: null,
          attributes: {
            idp: currentIdpRef.current?.idp,
            idp_key: currentIdpRef.current?.idpKey,
          },
        });
      }
    }
  }, 300);

  return {
    handleCallbackData,
    isAuthenticating,
    ssoOpener: {
      isOpen: isWaitingOnIDPPopup,
      isRedirectingToSSO,
      open: openSSO,
      focus: refocusSSOPopup,
      close: closeAndResetSSOPopup,
      createPopup,
    },
  };
};
